import { AlertTriangle } from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";

interface ErrorBannerProps {
  variant?: "blocking" | "warning" | "destructive";
  ariaLabel?: string;
  title: string;
  message?: string;
  diagnosticCode?: string;
  httpStatus?: number;
  shardUrl?: string;
  actionLabel?: string;
  onAction?: () => void;
}

export function ErrorBanner({
  variant = "warning",
  ariaLabel,
  title,
  message,
  diagnosticCode,
  httpStatus,
  shardUrl,
  actionLabel,
  onAction
}: ErrorBannerProps) {
  const blocking = variant === "blocking";
  const destructive = variant === "destructive" || blocking;

  return (
    <section
      role="alert"
      aria-label={ariaLabel}
      className={cn(
        "flex gap-3 border px-3 py-2 text-xs",
        blocking && "min-h-[360px] flex-col items-center justify-center text-center",
        destructive
          ? "border-destructive bg-surface-inset text-foreground"
          : "border-warning bg-warning-soft text-foreground"
      )}
    >
      <AlertTriangle
        aria-hidden="true"
        className={cn("shrink-0", blocking ? "h-9 w-9 text-destructive" : "h-4 w-4 text-warning")}
      />
      <div className={cn("min-w-0", blocking && "max-w-xl") }>
        <h2 className={cn("font-semibold", blocking && "text-base")}>{title}</h2>
        {message ? <p className="mt-1 text-muted-foreground">{message}</p> : null}
        {diagnosticCode || httpStatus !== undefined || shardUrl ? (
          <dl className={cn("mt-2 space-y-0.5", blocking && "text-left") }>
            {diagnosticCode ? (
              <div>
                <dt className="inline font-semibold">Diagnostic code: </dt>
                <dd className="inline font-mono">{diagnosticCode}</dd>
              </div>
            ) : null}
            {httpStatus !== undefined ? (
              <div>
                <dt className="inline font-semibold">HTTP status: </dt>
                <dd className="inline">{httpStatus}</dd>
              </div>
            ) : null}
            {shardUrl ? (
              <div>
                <dt className="inline font-semibold">Shard: </dt>
                <dd className="inline font-mono">{shardUrl}</dd>
              </div>
            ) : null}
          </dl>
        ) : null}
      </div>
      {actionLabel && onAction ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={cn("shrink-0", blocking ? "mt-2" : "ml-auto")}
          onClick={onAction}
        >
          {actionLabel}
        </Button>
      ) : null}
    </section>
  );
}
